import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { type FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";

const passwordSchema = z.object({
	password: z.string().min(1, "Please enter a password"),
});

type PasswordFormData = z.infer<typeof passwordSchema>;

interface PasswordDialogProps {
	isOpen: boolean;
	onSubmit: (password: string) => Promise<void>;
}

export const PasswordDialog: FC<PasswordDialogProps> = ({ isOpen, onSubmit }) => {
	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<PasswordFormData>({
		resolver: standardSchemaResolver(passwordSchema),
	});

	const onFormSubmit = async (data: PasswordFormData) => {
		try {
			await onSubmit(data.password);
		} catch (e) {
			setError("password", {
				type: "manual",
				message: e instanceof Error ? e.message : "Invalid password",
			});
		}
	};

	if (!isOpen) return null;

	const displayError = errors.password?.message;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
				{/* Header */}
				<div className="flex items-center gap-3 mb-4">
					<div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className="text-gray-600 dark:text-gray-400"
						>
							<rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
							<path d="M7 11V7a5 5 0 0 1 10 0v4" />
						</svg>
					</div>
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							Password Required
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							This document is password protected.
						</p>
					</div>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit(onFormSubmit)}>
					<div className="mb-4">
						<input
							type="password"
							{...register("password")}
							placeholder="Enter password"
							disabled={isSubmitting}
							className={`w-full px-3 py-2 border rounded-md outline-none transition-colors
								${displayError ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"}
								bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
								placeholder:text-gray-400 dark:placeholder:text-gray-500
								disabled:opacity-50 disabled:cursor-not-allowed`}
							autoFocus
						/>
						{displayError && (
							<p className="mt-2 text-sm text-red-500">{displayError}</p>
						)}
					</div>

					{/* Button */}
					<button
						type="submit"
						disabled={isSubmitting}
						className={`w-full px-4 py-2 rounded-md font-medium transition-colors
							${isSubmitting ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"}
						`}
					>
						{isSubmitting ? "Verifying..." : "Submit"}
					</button>
				</form>
			</div>
		</div>
	);
};
